/**
 * Multi-table session + favorites (browser local).
 * Left rail: tables you sit, favorites pin to top of lobby list.
 */

const OPEN_KEY = "budb_open_tables_v1";
const FAV_KEY = "budb_fav_tables_v1";

export type OpenTableEntry = {
  tableId: string;
  name: string;
  smallBlind: number;
  bigBlind: number;
  players?: number;
  maxPlayers?: number;
  /** Last known: action is on us */
  actionOnUs?: boolean;
  joinedAt: number;
  updatedAt: number;
};

export function listOpenTables(): OpenTableEntry[] {
  try {
    const raw = localStorage.getItem(OPEN_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as OpenTableEntry[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function upsertOpenTable(
  entry: Omit<OpenTableEntry, "joinedAt" | "updatedAt"> & {
    joinedAt?: number;
  },
): void {
  const list = listOpenTables().filter((t) => t.tableId !== entry.tableId);
  const prev = listOpenTables().find((t) => t.tableId === entry.tableId);
  list.unshift({
    ...entry,
    joinedAt: entry.joinedAt ?? prev?.joinedAt ?? Date.now(),
    updatedAt: Date.now(),
  });
  // Cap multi-table tabs
  localStorage.setItem(OPEN_KEY, JSON.stringify(list.slice(0, 8)));
}

export function setOpenTableAction(tableId: string, actionOnUs: boolean): void {
  const list = listOpenTables().map((t) =>
    t.tableId === tableId
      ? { ...t, actionOnUs, updatedAt: Date.now() }
      : t,
  );
  localStorage.setItem(OPEN_KEY, JSON.stringify(list));
}

export function removeOpenTable(tableId: string): void {
  localStorage.setItem(
    OPEN_KEY,
    JSON.stringify(listOpenTables().filter((t) => t.tableId !== tableId)),
  );
}

export function listFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function toggleFavorite(tableId: string): string[] {
  const set = new Set(listFavorites());
  if (set.has(tableId)) set.delete(tableId);
  else set.add(tableId);
  const out = [...set];
  localStorage.setItem(FAV_KEY, JSON.stringify(out));
  return out;
}

export function isFavorite(tableId: string): boolean {
  return listFavorites().includes(tableId);
}

/** Sort lobby tables: favorites first, then by stake */
export function sortTablesWithFavorites<
  T extends { id: string; stakes?: { bigBlind?: number } },
>(tables: T[], favs: string[]): T[] {
  const fav = new Set(favs);
  return [...tables].sort((a, b) => {
    const af = fav.has(a.id) ? 0 : 1;
    const bf = fav.has(b.id) ? 0 : 1;
    if (af !== bf) return af - bf;
    return (a.stakes?.bigBlind ?? 0) - (b.stakes?.bigBlind ?? 0);
  });
}
